import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { buildDreamTeam, scoreTeam, teamVerdict } from "../services/compatibility";
import { personAssignments, assignmentIsCurrent } from "../services/utilization";
import { getArchetype } from "../data/archetypes";
import "../styles.css";

function getInitials(name) { return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(); }
function getRoleClass(role) {
  const r = (role || "").toLowerCase();
  if (r.includes("supt")) return "supt";
  if (r.includes("pm")) return "pm";
  if (r.includes("apm")) return "apm";
  if (r.includes("fe")) return "fe";
  return "other";
}

const ROLE_OPTIONS = ["Sr. Supt", "Supt", "Asst. Supt", "FE", "PM", "APM"].sort((a, b) => a.localeCompare(b));

export default function DreamTeam() {
  const { people: peopleRaw, projects: projRaw, teamMembers, loading, error } = useData();
  const allPeople = (peopleRaw || []).filter(p => p.IsActive !== false && p.Archetype);
  const projects = (projRaw || []).filter(p => p.Status === "Active")
    .sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));
  const allMembers = teamMembers || [];

  // Who's currently deployed (so we can flag "would move from").
  // Time-aware: people whose assignments have ended are back on the bench.
  const deployment = {};
  allPeople.forEach(p => {
    const u = personAssignments(p, allMembers, projRaw || []);
    if (u.rows.length) deployment[p.FullName] = u.rows[0].jobNumber;
  });

  const [jobNumber, setJobNumber] = useState("");
  const [openRoles, setOpenRoles] = useState(["PM", "Supt"]);
  const [allowMoves, setAllowMoves] = useState(false);
  const [result, setResult] = useState(null);

  const activeJob = jobNumber || projects[0]?.Title || "";
  const members = allMembers.filter(m => m.ProjectJobNumber === activeJob && assignmentIsCurrent(m));
  const lockedPeople = members.map(m => allPeople.find(p => p.FullName === m.PersonName)).filter(Boolean);

  const benchNames = new Set(Object.keys(deployment));
  const bench = allPeople.filter(p => !benchNames.has(p.FullName));

  // Candidate pool = bench, plus (if allowMoves) deployed people not on this team
  const candidatePool = useMemo(() => {
    const onThisTeam = new Set(lockedPeople.map(p => p.FullName));
    let pool = bench.map(p => ({ ...p, _deployedOn: null }));
    if (allowMoves) {
      const movable = allPeople
        .filter(p => deployment[p.FullName] && deployment[p.FullName] !== activeJob && !onThisTeam.has(p.FullName))
        .map(p => ({ ...p, _deployedOn: deployment[p.FullName] }));
      pool = [...pool, ...movable];
    }
    return pool;
  }, [bench, allowMoves, allPeople, activeJob, lockedPeople]);

  const baseScore = lockedPeople.length >= 2 ? scoreTeam(lockedPeople)?.score : null;

  const handleBuild = () => {
    const r = buildDreamTeam(lockedPeople, openRoles, candidatePool);
    setResult(r);
  };

  const addRole = (role) => setOpenRoles(prev => [...prev, role]);
  const removeRole = (idx) => setOpenRoles(prev => prev.filter((_, i) => i !== idx));

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading projects…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Dream Team Maker</div>
          <div className="page-subtitle">Fill the open roles with the optimal people for the strongest team</div>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="bolt" /></div>
          <div className="empty-text">No active projects to build from yet.<br />Mark a project Active in the Admin console.</div>
        </div>
      ) : (
      <>
      {/* Setup */}
      <div className="card mb-24">
        <div className="grid-2-resp">
          <div>
            <label className="form-label">Start From Project</label>
            <select className="form-select" value={activeJob} onChange={e => { setJobNumber(e.target.value); setResult(null); }}>
              {projects.map(p => <option key={p.Title} value={p.Title}>{p.Title} — {p.JobName}</option>)}
            </select>

            <div style={{ marginTop: 16 }}>
              <label className="form-label">Locked (already on team)</label>
              <div className="dt-locked">
                {lockedPeople.length === 0 ? (
                  <span style={{ fontSize: 13, color: "var(--text-muted)" }}>No profiled members yet — build from scratch.</span>
                ) : lockedPeople.map(p => {
                  const m = members.find(mm => mm.PersonName === p.FullName);
                  return (
                    <div key={p.Id} className="dt-locked-chip">
                      <div className={`avatar ${getRoleClass(m?.ProjectRole)}`} style={{ width: 24, height: 24, fontSize: 9 }}>{getInitials(p.FullName)}</div>
                      <span>{p.FullName}</span>
                      <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{m?.ProjectRole}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <label className="form-label">Open Roles to Fill</label>
            <div className="dt-open-roles">
              {openRoles.map((r, i) => (
                <div key={i} className="dt-open-chip">
                  {r}
                  <button onClick={() => removeRole(i)} className="dt-remove">×</button>
                </div>
              ))}
            </div>
            <div className="dt-add-roles">
              {ROLE_OPTIONS.map(r => (
                <button key={r} className="dt-add-btn" onClick={() => addRole(r)}>+ {r}</button>
              ))}
            </div>

            <label className="dt-toggle">
              <input type="checkbox" checked={allowMoves} onChange={e => { setAllowMoves(e.target.checked); setResult(null); }} />
              <span>Allow moving people from other projects</span>
            </label>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
              Candidate pool: {candidatePool.length} {allowMoves ? "(bench + movable)" : "(bench only)"}
            </div>

            <button className="btn btn-primary" style={{ width: "100%", marginTop: 16 }} onClick={handleBuild} disabled={openRoles.length === 0}>
              <Icon name="bolt" size={14} style={{ marginRight: 6, verticalAlign: "middle" }} /> Build Dream Team
            </button>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <>
          <div className="dt-result-header">
            <div className="dt-score-block">
              <div className="dt-score-num" style={{ color: result.finalScore >= 75 ? "var(--green)" : result.finalScore >= 55 ? "var(--gold-text)" : "var(--red)" }}>
                {result.finalScore}
              </div>
              <div className="dt-score-label">{teamVerdict(result.finalScore || 0)}</div>
            </div>
            {baseScore != null && (
              <div className="dt-improvement">
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Improvement</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: result.finalScore - baseScore >= 0 ? "var(--green)" : "var(--red)" }}>
                  {result.finalScore - baseScore >= 0 ? "+" : ""}{result.finalScore - baseScore} pts
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>from {baseScore} base</div>
              </div>
            )}
            <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-secondary)", maxWidth: 280 }}>
              Filled <b style={{ color: "var(--text-primary)" }}>{result.fills.filter(f => f.person).length}</b> of {result.fills.length} open roles from your available pool.
            </div>
          </div>

          <div className="section-title mb-16" style={{ marginTop: 24 }}>Recommended Fills</div>
          {result.fills.map((f, i) => {
            if (!f.person) {
              return (
                <div key={i} className="dt-fill-row empty">
                  <div className="dt-fill-role">{f.role}</div>
                  <div style={{ flex: 1, color: "var(--red)", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name="user-x" size={16} /> No one available — recruit for this spot
                  </div>
                  <Link to="/hiring" className="btn btn-ghost" style={{ fontSize: 12, padding: "5px 12px" }}>See Target Profile</Link>
                </div>
              );
            }
            const arch = getArchetype(f.person.Archetype);
            return (
              <div key={i} className="dt-fill-row">
                <div className="dt-fill-role">{f.role}</div>
                <div className={`avatar ${getRoleClass(f.role)}`} style={{ width: 38, height: 38, fontSize: 12 }}>{getInitials(f.person.FullName)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                    {f.person.FullName}
                    {f.moved && <span className="dt-moved-tag">⤳ from {f.moved}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                    <Icon name={arch.icon} size={12} /> {f.person.Archetype}
                  </div>
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginRight: 12 }}>
                  A{f.person.Survey_A} B{f.person.Survey_B} C{f.person.Survey_C} D{f.person.Survey_D}
                </div>
                <div style={{ textAlign: "right", minWidth: 50 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "var(--green)" }}>{f.delta >= 0 ? "+" : ""}{f.delta}</div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>impact</div>
                </div>
              </div>
            );
          })}

          <div className="dt-claude-cta">
            <Icon name="brain" size={18} />
            <span>Want the reasoning? Open this team in <Link to={`/projects/${encodeURIComponent(activeJob)}`} style={{ color: "var(--gold-text)", fontWeight: 600 }}>Project Analysis</Link> and run the AI narrative on the proposed roster.</span>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}
